import React from "react";
import { X, CreditCard, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "./api";

const fmtTl = (n) => `₺${Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Kredi kartı ile ödeme: komisyon eklenir, iyzico ödeme sayfası açılır.
export function CardPayModal({ customerId, customerName, orderId, orderNo, defaultAmount, onClose }) {
  const [cfg, setCfg] = React.useState(null);
  const [amount, setAmount] = React.useState(Number(defaultAmount || 0).toFixed(2));
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    api.get("/payments/card/config")
      .then(({ data }) => setCfg(data))
      .catch(() => setCfg({ commissionRate: 3.19, fixedFee: 0.49, configured: false }));
  }, []);

  const net = Number(String(amount).replace(",", ".")) || 0;
  const commission = cfg ? Number((net * cfg.commissionRate / 100 + cfg.fixedFee).toFixed(2)) : 0;
  const total = Number((net + commission).toFixed(2));

  const pay = async () => {
    if (net <= 0) { toast.error("Geçerli bir tutar girin"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/card/init", {
        customerId, orderId: orderId || "", amount: net,
        description: orderNo ? `Sipariş ${orderNo}` : `Cari ödeme — ${customerName || ""}`,
      });
      if (!data.paymentPageUrl) throw new Error("Ödeme sayfası alınamadı");
      window.location.assign(data.paymentPageUrl);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "iyzico ödeme formu başlatılamadı");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4" data-testid="card-pay-modal">
      <div className="zen-panel w-full max-w-md zen-anim-up text-slate-900 dark:text-slate-100">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-[#22304A] flex items-center justify-between">
          <h3 className="text-base font-bold flex items-center gap-2">
            <CreditCard className="w-4.5 h-4.5 text-[#E5322A]" /> Kredi Kartı ile Ödeme
          </h3>
          <button onClick={onClose} data-testid="card-pay-close-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-xs text-slate-500">
            {customerName}{orderNo ? ` · Sipariş ${orderNo}` : " · Cari ödeme"}
          </div>

          <div>
            <label className="text-[11px] font-semibold text-slate-500 block mb-1">Ödenecek Tutar (TL)</label>
            <input
              data-testid="card-pay-amount-input"
              className="zen-input w-full zen-mono"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
            />
          </div>

          {cfg === null ? (
            <div className="text-xs text-slate-500 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Komisyon bilgisi alınıyor...</div>
          ) : (
            <div className="rounded-xl bg-slate-100 dark:bg-[#0C1220] p-3 space-y-1.5 text-sm" data-testid="card-pay-breakdown">
              <div className="flex justify-between"><span className="text-slate-500">Cariye işlenecek net tutar</span><span className="zen-mono font-semibold">{fmtTl(net)}</span></div>
              <div className="flex justify-between">
                <span className="text-slate-500">Komisyon (%{cfg.commissionRate} + {fmtTl(cfg.fixedFee)})</span>
                <span className="zen-mono font-semibold">{fmtTl(commission)}</span>
              </div>
              <div className="flex justify-between pt-1.5 border-t border-slate-200 dark:border-[#22304A]">
                <span className="font-bold">Karttan çekilecek toplam</span>
                <span className="zen-mono font-bold text-[#E5322A]" data-testid="card-pay-total">{fmtTl(total)}</span>
              </div>
            </div>
          )}

          {cfg && !cfg.configured && (
            <div className="text-xs text-amber-600 bg-amber-500/10 rounded-lg p-2.5">
              iyzico API anahtarları eksik. Ödeme başlatılamaz — anahtarları ekledikten sonra tekrar deneyin.
            </div>
          )}

          <div className="text-[11px] text-slate-500 flex items-start gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-emerald-500 shrink-0" />
            Kart bilgileri iyzico'nun güvenli ödeme sayfasında girilir, sistemimizde saklanmaz. Ödeme onaylanınca cariye net
            tutar alacak olarak işlenir, sipariş otomatik olarak İmalatta aşamasına geçer.
          </div>
        </div>

        <div className="px-5 py-4 border-t border-slate-200 dark:border-[#22304A] flex justify-end gap-2">
          <button onClick={onClose} className="zen-btn-ghost !text-xs">Vazgeç</button>
          <button onClick={pay} disabled={busy || !cfg?.configured} data-testid="card-pay-submit-btn" className="zen-btn-primary !text-xs">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
            Ödeme Sayfasına Git
          </button>
        </div>
      </div>
    </div>
  );
}

// Ödeme sonucu geri dönüşü: /?payment=success|failed|error&ref=...
export function PaymentResultToast() {
  React.useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const st = p.get("payment");
    if (!st) return;
    const ref = p.get("ref");
    if (st === "success") {
      api.get(`/payments/card/${ref}`)
        .then(({ data }) => toast.success(`Ödeme alındı — ${fmtTl(data.netAmount)} cariye işlendi (iyzico ref: ${data.iyzicoPaymentId || ref})`, { duration: 8000 }))
        .catch(() => toast.success("Ödeme başarıyla alındı", { duration: 6000 }));
    } else if (st === "failed") {
      toast.error("Ödeme tamamlanamadı — kart reddedildi veya işlem iptal edildi", { duration: 8000 });
    } else {
      toast.error("Ödeme sonucu doğrulanamadı", { duration: 6000 });
    }
    window.history.replaceState({}, "", window.location.pathname);
  }, []);
  return null;
}

// Ayarlar > Ödeme Ayarları (sadece admin): komisyon oranı + sabit işlem ücreti
const FIELDS = [
  { key: "iyzico_commission_rate", label: "Komisyon Oranı (%)", def: "3.19" },
  { key: "iyzico_fixed_fee", label: "Sabit İşlem Ücreti (TL)", def: "0.49" },
];

export function PaymentSettings() {
  const [rows, setRows] = React.useState([]);
  const [vals, setVals] = React.useState({});
  const [cfg, setCfg] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  const load = async () => {
    const [{ data }, c] = await Promise.all([
      api.get("/settings", { params: { category: "payment" } }),
      api.get("/payments/card/config").catch(() => ({ data: null })),
    ]);
    setRows(data || []);
    setCfg(c?.data || null);
    setVals(Object.fromEntries(FIELDS.map((f) => {
      const r = (data || []).find((x) => x.key === f.key);
      return [f.key, r ? r.value : f.def];
    })));
  };
  React.useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      for (const f of FIELDS) {
        const existing = rows.find((r) => r.key === f.key);
        if (existing) await api.put(`/settings/${existing.id}`, { value: String(vals[f.key] ?? f.def) });
        else await api.post("/settings", { category: "payment", key: f.key, value: String(vals[f.key] ?? f.def), label: f.label });
      }
      toast.success("Kaydedildi");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setSaving(false); }
  };

  const rate = Number(String(vals.iyzico_commission_rate || 0).replace(",", ".")) || 0;
  const fee = Number(String(vals.iyzico_fixed_fee || 0).replace(",", ".")) || 0;
  const sample = Number((1000 * rate / 100 + fee).toFixed(2));

  return (
    <div className="zen-panel p-6 space-y-4" data-testid="payment-settings">
      <div>
        <h3 className="text-base font-bold flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#E5322A]" /> Ödeme Ayarları (iyzico)</h3>
        <p className="text-xs text-slate-500 mt-1">
          Kredi kartı ile ödemede müşteriden tahsil edilecek komisyon. Cari hesaba her zaman net tutar işlenir.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-[11px] font-semibold text-slate-500 block mb-1">{f.label}</label>
            <input
              data-testid={`payment-field-${f.key}`}
              className="zen-input !text-sm w-full zen-mono"
              value={vals[f.key] ?? ""}
              onChange={(e) => setVals((p) => ({ ...p, [f.key]: e.target.value }))}
              inputMode="decimal"
            />
          </div>
        ))}
      </div>

      <div className="rounded-lg bg-slate-100 dark:bg-[#0C1220] p-3 text-xs text-slate-500" data-testid="payment-settings-sample">
        Örnek: ₺1.000,00 ödemede komisyon <b className="text-slate-700 dark:text-slate-200">{fmtTl(sample)}</b> → karttan çekilecek toplam{" "}
        <b className="text-slate-700 dark:text-slate-200">{fmtTl(1000 + sample)}</b>, cariye işlenen net{" "}
        <b className="text-slate-700 dark:text-slate-200">{fmtTl(1000)}</b>
      </div>

      {cfg && (
        <div className={`text-xs rounded-lg p-2.5 ${cfg.configured ? "text-emerald-600 bg-emerald-500/10" : "text-amber-600 bg-amber-500/10"}`}>
          {cfg.configured
            ? `iyzico bağlantısı hazır · ortam: ${cfg.env === "sandbox" ? "Sandbox (test)" : "Canlı"}`
            : "iyzico API anahtarları eksik (IYZICO_API_KEY / IYZICO_SECRET_KEY) — ödeme başlatılamaz."}
        </div>
      )}

      <div className="pt-2 border-t border-slate-200 dark:border-[#22304A]">
        <button onClick={save} disabled={saving} data-testid="payment-settings-save-btn" className="zen-btn-primary !text-xs">
          {saving ? "Kaydediliyor..." : "Kaydet"}
        </button>
      </div>
    </div>
  );
}
